import { useEffect, useMemo, useRef } from "react";
import { useTransactionStatus } from "@/api/api";
import useVisibilityAwarePoll from "@/hooks/useVisibilityAwarePoll";
import { DEFAULT_TRANSACTION_INTERVAL_MS } from "@/utils/constants";

interface UseTransactionPollingOptions {
  readonly enabled?: boolean;
  readonly intervalMs?: number;
  /**
   * Set for transactions whose execution is coupled to being polled (Gemini image/video
   * generation - see `startVisibilityAwarePoller`) so they keep polling on a backgrounded tab
   * instead of stalling. Defaults to false: most transactions here (script/scene/styleDNA text
   * jobs, RunwayML generation) run on a worker regardless of whether anyone is polling.
   */
  readonly criticalWhileHidden?: boolean;
}

const isNonEmptyString = (value: unknown): value is string => typeof value === "string" && value.length > 0;

/**
 * Poll transaction status every `intervalMs` while the backend returns 202 (pending).
 * Stops automatically once the transaction is completed (200) or errors.
 */
const useTransactionPolling = (transactionId: string | null, options: UseTransactionPollingOptions = {}) => {
  const { enabled = true, intervalMs = DEFAULT_TRANSACTION_INTERVAL_MS, criticalWhileHidden = false } = options;

  const transactionQuery = useTransactionStatus(transactionId, {
    enabled: enabled && isNonEmptyString(transactionId)
  });

  const status = transactionQuery.data?.status ?? null;
  const isQueryError = transactionQuery.isError;
  const refetchRef = useRef(transactionQuery.refetch);
  useEffect(() => {
    refetchRef.current = transactionQuery.refetch;
  }, [transactionQuery.refetch]);

  const shouldPoll = enabled && isNonEmptyString(transactionId) && !isQueryError && status === "pending";
  useVisibilityAwarePoll(
    async () => {
      await refetchRef.current();
    },
    intervalMs,
    { enabled: shouldPoll, criticalWhileHidden }
  );

  return useMemo(
    () => ({
      transactionQuery,
      status,
      isPolling: enabled && status === "pending" && !isQueryError,
      isPending: status === "pending" && !isQueryError,
      isCompleted: status === "completed",
      isErrored: status === "error" || isQueryError
    }),
    [enabled, status, isQueryError, transactionQuery]
  );
};

export default useTransactionPolling;
